import { useEnvStore } from '@remote/store/env'
import { IParams, ITask } from '@remote/type'
import { isProd } from '@remote/api/config'
import { isAndroidNative, sendMessage, isIOSNative } from '@xunlei/universal-native-api'
import global from '@remote/store/constantStore'
import { store } from '@remote/store'
import { isMagnetTypeBTLink, isOtherTypeBTLink } from '@remote/api/adapter/helper'
import { toast as showToast } from '@remote/utils/dialog'

/**
 * 将一个支持callback的函数转换成支持promise的
 * @method promisefy
 * @param {Function} fn - 支持callback的函数
 * @param {Object} context - 执行环境
 * @returns {Function}
 */
export function promisefy (fn, context) {
  return function (...args) {
    return new Promise((resolve, reject) => {
      const cb = (err, payload) => {
        if (err) {
          reject(err)
        } else {
          resolve(payload)
        }
      }
      fn.apply(context, [...args, cb])
    })
  }
}

/**
 * @method getQueryString
 * @desc 获取链接参数的值
 * @param url {String} - 链接地址
 * @param name {String} - 参数名称
 * @returns {String} - 参数值
 */
export function getQueryString (url, name) {
  if (!url || !name) return
  return url.match(new RegExp(`(^${name}|[?|&]${name})=([^&#]+)`))
    ? RegExp.$2
    : ''
}

/**
 *
 * @param {Number} bytes 字节数
 * @param {Number} point 小数点保留的位置
 */
export function bytesToSize (bytes: number, point = 1) {
  if (Number(bytes) <= 0) {
    return '0 B'
  }
  const k = 1024 // or 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const val = bytes / Math.pow(k, i)
  let size = val.toPrecision(3)
  size = val.toFixed(i === 0 ? 0 : point)
  return size + sizes[i]
}

export function formatTime (dateObj: Date | string, fmt = 'yyyy-MM-dd hh:mm') {
  if (typeof dateObj === 'string') {
    dateObj = new Date(dateObj)
  }
  const o = {
    'M+': dateObj.getMonth() + 1, // 月份
    'd+': dateObj.getDate(), // 日
    'h+': dateObj.getHours(), // 小时
    'm+': dateObj.getMinutes(), // 分
    's+': dateObj.getSeconds(), // 秒
    'q+': Math.floor((dateObj.getMonth() + 3) / 3), // 季度
    S: dateObj.getMilliseconds(), // 毫秒
  }
  if (/(y+)/.test(fmt)) {
    fmt = fmt.replace(
      RegExp.$1,
      (dateObj.getFullYear() + '').substr(4 - RegExp.$1.length),
    )
  }
  for (const k in o) {
    if (new RegExp('(' + k + ')').test(fmt)) {
      fmt = fmt.replace(
        RegExp.$1,
        RegExp.$1.length == 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length),
      )
    }
  }
  return fmt
}

/** 节流函数 保证在mustRun毫秒内,必然触发一次 handler */
export function throttle (func, wait, mustRun) {
  let timeout
  let startTime = Date.now()
  return function () {
    // @ts-ignore
    const context = this
    const args = arguments
    const curTime = Date.now()
    clearTimeout(timeout)
    if (curTime - startTime >= mustRun) {
      func.apply(context, args)
      startTime = curTime
    } else {
      timeout = setTimeout(func, wait)
    }
  }
}

export const once = (fn) => {
  let called = false
  return function (...args) {
    if (called) return
    called = true
    // @ts-ignore
    return fn.apply(this, args)
  }
}

export function formatSize (size, places = 1) {
  size = parseInt(size)
  if (!size) {
    return '0B'
  }
  const sizeMap = ['B', 'KB', 'MB', 'GB', 'TB']
  let index = 0
  while (size >= 1024) {
    size = size / 1024
    index++
  }
  if (index < 2) {
    // B、KB不展示小数
    size = String(Math.floor(size))
  } else {
    const stringSize = String(size)
    const dotIndex = stringSize.indexOf('.')
    size = dotIndex > 0 ? stringSize.slice(0, stringSize.indexOf('.') + 1 + places) : size
    // size = String(size.toFixed(places))
  }
  // if (/(.0)/.test(size)) size = size.split('.0')[0]
  return `${size}${sizeMap[index]}`
}

/**
 *
 *
 * @export
 * @param {number} millisecond 持续时间 ms
 * @returns Promise<void>
 */
export function sleep (millisecond) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(null)
    }, millisecond)
  })
}

export function checkValidLink (url) {
  // return /^(thunder|magnet|http[s]?|ftp|ed2k):/i.test(url)
  return isMagnetTypeBTLink(url) || isOtherTypeBTLink(url)
}

export function checkIsJSON (str) {
  if (typeof str === 'string') {
    try {
      const obj = JSON.parse(str)
      if (typeof obj === 'object' && obj) {
        return true
      } else {
        return false
      }
    } catch (e) {
      return false
    }
  }
  return false
}

export function joinParams (url: string, params: IParams) {
  if (!/\?/.test(url)) {
    url += '?'
  }
  const keyValues = Object.keys(params || {}).map((key) => {
    return `${key}=${encodeURIComponent(params[key])}`
  })
  return url + keyValues.join('&')
}

export function task_param (task: any, name: string) {
  if (!task.params) {
    return ''
  }

  if (!task.params[name]) {
    return ''
  }
  return task.params[name]
}

export function phase (p: string) {
  const a = new Map([
    ['PHASE_TYPE_COMPLETE', '完成'],
    ['PHASE_TYPE_PENDING', '等待中'],
    ['PHASE_TYPE_RUNNING', '运行中'],
    ['PHASE_TYPE_ERROR', '错误'],
  ])
  return a.get(p)
}

export const uniqueObject = function (list: ITask[], key: string) {
  return list.filter((item, index, arr) => {
    return arr.findIndex(v => v[key] === item[key]) === index
  })
}

// 从秒转成分、钟时间
export function msToTime (ms: number) {
  if (ms <= 60) return '1分钟'
  else if (ms <= 60 * 60) return Math.ceil(ms / 60) + '分钟'
  else if (ms <= 60 * 60 * 24) return Math.ceil(ms / 3600) + '小时'
  else return Math.ceil(ms / (60 * 60 * 24)) + '天'
}
export function formatLastTime (timestamp: number) {
  const hours = Math.floor(timestamp / 3600)
  const minutes = Math.floor((timestamp - (hours * 3600)) / 60)
  const seconds = timestamp - (hours * 3600) - (minutes * 60)

  const formattedHours = hours.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')
  const formattedSeconds = seconds.toString().padStart(2, '0')

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
}
// 判断数组1是否包含了数组2
export function listIncludes (arr1: string[], arr2: string[]) {
  return arr2.every(val => arr1.includes(val))
}

export function isDebugMode () {
  return !isProd || getQueryString(typeof window !== 'undefined' && window.location.href, 'debug')
}

export function rewirteLog () {
  console.log = (function (log) {
    return !isProd ? log : function () { }
  }(console.log))
}

export function toast (message = '未知错误，请稍后再试') {
  // if (isAndroid) {
  //   sendMessage({ platform: 'android', name: 'xlShowToast', params: { message } }, { noCallback: true })
  // } else if (isIOS) {
  //   sendMessage({ platform: 'ios', name: 'toastMessage', params: { message } }, { noCallback: true })
  if (isAndroidNative) {
    sendMessage({ platform: 'android', name: 'xlShowToast', params: { message } }, { noCallback: true })
  } else if (isIOSNative) {
    sendMessage({ platform: 'ios', name: 'toastMessage', params: { message } }, { noCallback: true })
  } else {
    showToast(message)
  }
}
export function parseTime (time, cFormat: string | null = null) {
  if (arguments.length === 0) {
    return null
  }
  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}'
  let date
  if (typeof time === 'object') {
    date = time
  } else {
    if ((typeof time === 'string') && (/^[0-9]+$/.test(time))) {
      time = parseInt(time)
    }
    if ((typeof time === 'number') && (time.toString().length === 10)) {
      time = time * 1000
    }
    date = new Date(time)
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay(),
  }
  const timeStr = format.replace(/{([ymdhisa])+}/g, (result, key) => {
    const value = formatObj[key]
    // Note: getDay() returns 0 on Sunday
    if (key === 'a') { return ['日', '一', '二', '三', '四', '五', '六'][value] }
    return value.toString().padStart(2, '0')
  })
  return timeStr
}

export function isToday (someDate) {
  someDate = new Date(someDate)
  console.log(someDate)
  const today = new Date()
  return someDate.getDate() === today.getDate() &&
    someDate.getMonth() === today.getMonth() &&
    someDate.getFullYear() === today.getFullYear()
}

// 解析的资源转成目录列表
export function resolveTaskTree (list: any[] = []): any[] {
  return list ? list.map(v => ({
    checkType: 0,
    id: v.id,
    title: v.name,
    size: bytesToSize(v.file_size || v.size),
    file_size: v.file_size || v.size,
    count: v.file_count || 0,
    icon: v.meta && v.meta.icon ? v.meta.icon : 'https://backstage-img-ssl.a.88cdn.com/61846e5397640d721045124e4d504b586a2d493f',
    parent_id: v.parent_id,
    isDir: !!v.dir || !!v.children,
    file_index: v.file_index || 0,
    children: ((v.dir && v.dir.resources || v.children) ? resolveTaskTree(v.dir?.resources || v.children) : []),
  })) : []
}


export function str2date (str: string | number) {
  // 20240223  ->  2024-02-23
  const strs = String(str).split('')
  strs.splice(4, 0, '-')
  strs.splice(7, 0, '-')
  return strs.join('')
}

//判断是否是微信
export function isWeixin () {
  const ua = navigator.userAgent.toLowerCase()
  const re = new RegExp(/MicroMessenger/i)
  return re.test(ua)
}

// 判断是否是可以播放的格式
export function isTruePlayFormat (str: string) {
  const re = new RegExp(/\.(wmv|asf|asx|rm|rmvb|mpg|mpeg|mpe|3gp|mov|mp4|m4v|avi|dat|mkv|flv|f4v|vob|ts|xv)$/i)
  return re.test(str)
}

// 判断是否是图片
export function isTruePicFormat (str: string) {
  const re = new RegExp(/\.(bmp|jpg|png|tif|gif|pcx|tga|exif|fpx|svg|psd|cdr|pcd|dxf|ufo|eps|ai|raw|WMF|webp|apng)$/i)
  return re.test(str)
}

/**
 * @method 获取localStorage中某个的数据
 * @param {*key值} key
 */
export function getLocalValue (key: string) {
  try {
    key = 'yc.' + key
    const value = JSON.parse(localStorage.getItem(key) || '{}')
    if (new Date().getTime() < value.time + 1000 * 24 * 3600) {
      return value.value
    } else {
      localStorage.removeItem(key)
      return null
    }
  } catch (e) {
    console.log('从localStorage获取' + key + '失败: ' + e)
  }
}

/**
 * @method 删除localStorage中某个的数据
 * @param {*key值} key
 */
export function removeLocalValue (key: string) {
  try {
    key = 'yc.' + key
    localStorage.removeItem(key)
  } catch (e) {
    console.log('从localStorage删除' + key + '失败: ' + e)
  }
}

/**
 * @method 存储数据到localStorage中
 * @param {*key值} key
 * @param {*值} value
 */
export function setLocalValue (key: string, value: any) {
  key = 'yc.' + key
  const currentTime = new Date().getTime()
  localStorage.setItem(key, JSON.stringify({
    value,
    time: currentTime,
  }))
}

/**
 * @method 存储数组到localStorage中
 * @param {*key值} key
 * @param {*值} value
 */
export function popLocalValue (key: string, value: any) {
  const list = getLocalValue(key) || []
  const length = list.length
  if (length > 30) {
    list.pop()
  }
  list.unshift(value)
  setLocalValue(key, list)
}

// 判断客户端
export function judgeClient () {
  const ua = navigator.userAgent
  let client = ''
  if (/(iPhone|iPad)/i.test(ua)) {
    client = 'ios'
  } else if (/(Android)/i.test(ua)) {
    client = 'an'
  } else {
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && /(Mac)/.test(ua)) {
      client = 'mac'
    } else {
      client = 'pc'
    }
  }
  return client
}

export function parseSearch (url = location.search): Record<string, any> {
  const theRequest = new Object()
  if (url.indexOf('?') != -1) {
    const str = url.substr(1)
    const strs = str.split('&')
    for (let i = 0; i < strs.length; i++) {
      theRequest[strs[i].split('=')[0]] = unescape(strs[i].split('=')[1])
    }
  }
  return theRequest
}

export function stringifySearch (params: { [key: string]: any }) {
  return location.pathname + '?' + new URLSearchParams(params).toString() + location.hash
}

export function isHUAWEIEnv () {
  // 华为安卓: window.hdc
  // 华为iOS: !!window?.webkit?.messageHandlers?.hdc
  return Boolean(window.hdc) || Boolean(window?.webkit?.messageHandlers?.hdc)
}

/**
 * 判断是否在厂商App里。
 * 有的厂商app提供了android，ios，webnative这样的（具体是哪个厂商，难以考证）
 * window.hdc如果存在，说明在华为App里。
 * isIframe为什么需要返回true，暂时不清楚，先保持不动。
 */
export function isNativeApp () {
  return !!window.android || !!window.ios || !!window.webnative || isHUAWEIEnv() || isIframe()
}

/**
 * 是否在手机迅雷app内打开
 */
export function isInXunleiApp () {
  return isAndroidNative || isIOSNative
}

/**
 * 是否在PC迅雷11内打开
 */
export function isInPCXunlei () {
  return window.navigator.userAgent.includes('TBC')
}

/**
 * 是否在手机迅雷或PC迅雷中打开
 */
export function isXunlei () {
  return isInXunleiApp() || isInPCXunlei()
}

export function nativeCall (name: string, ...args: unknown[]) {
  const func = window.android || window.ios || window.webnative
  console.log('APP 调用:', name, ...args)
  if (global.isMiniProgram) {
    miniProgramCall({ name, params: args })
  } else if (isIframe()) {
    iframeCall({ name, params: args })
  } else {
    func[name] && func[name](...args)
  }
}

export function isIframe () {
  return top !== self
}

export function iframeCall (data: object) {
  window.parent.postMessage(JSON.stringify(data), '*')
}

export function miniProgramCall (data: object) {
  window.miniProgram.postMessage({ data })
}

export async function ping (url: string) {
  try {
    // await fetch(url, { method: 'HEAD' })
    const res = await sendMessage({
      platform: 'android', name: 'xlHttpRequestForward', params: {
        method: 'HEAD',
        timeout: 500,
        url,
      },
    })
    console.log('ping', res)
    return true
  } catch (e) {
    return false
  }
}

export function isMagnetURL (s: string): boolean {
  if (s.length < 'magnet:?'.length) {
    return false
  }
  const strings = s.toLowerCase()
  if (strings.indexOf('magnet:?') >= 0 || strings.indexOf('thunder://quftywduzxq6') >= 0) {
    return true
  }
  return false
}
export async function isMiniProgramPromise () {
  return new Promise((resolve) => {
    if (/MicroMessenger/i.test(navigator.userAgent)) {
      window.wx.miniProgram.getEnv((res: any) => {
        if (res.miniprogram) {
          resolve(true)
        } else {
          resolve(false)
        }
      })
    } else {
      resolve(false)
    }
  })
}
/**
 * @description 补齐迅雷磁力链接
 * @param text 
 * @returns {string}
 */
export const getThunderLink = (text: string) => {
  const copyText = text.toString().trim()
  const reg = /^[0-9a-zA-Z]{40}$/
  const reg_short = /^[0-9a-zA-Z]{32}$/
  let value = copyText
  if (reg.test(copyText) || reg_short.test(copyText)) {
    value = 'magnet:?xt=urn:btih:' + copyText
  }
  return value
}

/**
 * 从downloads中创建aliasMap
 */
export const createAliasMapFromDownloads = (downloads: API_DEVICE.DownloadInfo[] = []) => {
  const aliasMap: Record<string, string> = {}
  downloads.forEach(i => {
    if (i.path && i.name) {
      Object.assign(aliasMap, {
        [i.path]: i.name,
      })
    }
  })
  return aliasMap
}

/**
 * 替换根路径，开头匹配
 */
export const replaceRootAlias = ({ path, aliasMap = {} }: { path: string; aliasMap: Record<string, string> }) => {
  const aliasOption = Object.keys(aliasMap)
  if (aliasOption.length > 0) {
    for (const alias of aliasOption) {
      if (path.indexOf(alias) === 0) {
        return path.replace(alias, aliasMap[alias])
      }
    }
  }
  return path
}


export const updateGlobalProperties = (query) => {
  console.log('更新全局属性', query)
  const envStore = useEnvStore()
  envStore.updateEnv(query)

  const fromSpace = query.space || ''
  const runnerSpace = query.runner_space || ''
  const platform = query.platform || ''

  global.fromApp = query.fromApp || ''
  global.fromSpace = fromSpace
  global.error_message = query.error_message || ''

  runnerSpace && store.commit('update', { runnerSpace })
  store.commit('update', { fromSpace })
  store.commit('update', { platform })
  store.commit('update', {
    // 只显示当前一台设备
    justOne: Number(query.justOne || query.justone || '0'),
  })
  console.log('更新全局属性结束', global, store)
}

// 调用手机震动，并处理兼容性问题。
// 因为 iOS safari 没有这个 API 所以 catch 一下错误。
export const vibrate = (pattern: number[]) => {
  try {
    window.navigator.vibrate(pattern)
  } catch (error) {
    // eslint-disable-next-line no-empty
  }
}

export function guid (): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0,
      v = c == 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

export function reduceFilename  (filename: string) {
  return filename.replace(/([【].*?)(www\.[a-zA-Z0-9.]+)(.*?|】\])|(www\.[a-zA-Z0-9.]+)/, '...')
}